import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';


@Component({
  selector: 'app-fee-schedule',         
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective],
  providers: [],
  templateUrl: './fee-schedule.component.html',
  styleUrl: './fee-schedule.component.scss',
  changeDetection:ChangeDetectionStrategy.Default,

})
export class FeeScheduleComponent implements OnChanges {

  feeScheduleForm: FormGroup;

  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder) {
    this.feeScheduleForm = this.fb.group({
      initialBalance: [10000000],
         discount: [0],
          flatFee: [0],
    });
  }

  hasDiscountOrFlatFee = false;
  feeOverrideEnabled = false;
  
  private _preventClear = false;
  /** True when flat fee was explicitly provided (including 0) by the parent or user. */
  private _flatFeeExplicit = false;
  private _formReady = false;

  ngOnInit() {
    this._formReady = true;
    if (this.initialBalance > 0) {
      this.feeScheduleForm.patchValue({ initialBalance: this.initialBalance }, { emitEvent: false });
    }
    if (this.initialFlatFee !== null) {
      this.feeScheduleForm.patchValue({ flatFee: this.initialFlatFee }, { emitEvent: false });
      this._flatFeeExplicit = true;
      this.feeOverrideEnabled = true;
    } else if (this.initialDiscount > 0) {
      this.feeScheduleForm.patchValue({ discount: this.initialDiscount }, { emitEvent: false });
      this.feeOverrideEnabled = true;
    }

    // When discount changes, clear flat fee then recalculate
    this.feeScheduleForm.get('discount')!.valueChanges.subscribe(() => {
      if (this._preventClear) return;
      this._flatFeeExplicit = false;
      this.feeScheduleForm.patchValue({ flatFee: 0 }, { emitEvent: false });
      this.calculateFeeSchedule();
    });

    // When flat fee changes, clear discount then recalculate
    this.feeScheduleForm.get('flatFee')!.valueChanges.subscribe(val => {
      if (this._preventClear) return;
      // Treat any non-null/undefined value (including 0) as an explicit entry
      this._flatFeeExplicit = val !== null && val !== undefined;
      this.feeScheduleForm.patchValue({ discount: 0 }, { emitEvent: false });
      this.calculateFeeSchedule();
    });

    // When asset amount changes, recalculate
    this.feeScheduleForm.get('initialBalance')!.valueChanges.subscribe(() => {
      this.calculateFeeSchedule();
    });

    this.calculateFeeSchedule();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialBalance'] && !changes['initialBalance'].firstChange) {
      this.feeScheduleForm.patchValue({ initialBalance: this.initialBalance }, { emitEvent: false });
      this.calculateFeeSchedule();
    }
  }

  @Input() initialBalance=0;
  @Input() showDiscount=false;
  @Input() showFlatFee=false;
  @Input() initialDiscount=0;

  private _initialFlatFee: number | null = null;
  @Input()
  set initialFlatFee(val: number | null) {
    this._initialFlatFee = val;
    if (this._formReady) {
      // Route through setValue so it goes through the same valueChanges path as user input:
      // null = "clearing the field" → _flatFeeExplicit=false, standard schedule
      // 0    = "entering zero"      → _flatFeeExplicit=true,  100% discount
      this.feeScheduleForm.get('flatFee')!.setValue(val);
    }
  }
  get initialFlatFee(): number | null { return this._initialFlatFee; }

  @Output() annualPercentageFee:EventEmitter<number> = new EventEmitter<number>();
  @Output() flatFeeChange: EventEmitter<number | null> = new EventEmitter<number | null>();
  @Output() discountChange: EventEmitter<number> = new EventEmitter<number>();

    feeSchedule:any=[
    {limit:500000,  fee:0.90, text:"first $500,000"},
    {limit:500000,  fee:0.75, text:"next $500,000"},
    {limit:1000000, fee:0.60, text:"next $1 million"},
    {limit:2000000, fee:0.45, text:"next $2 million"},
    {limit:2000000, fee:0.30, text:"next $2 million"},
    {limit:4000000, fee:0.25, text:"next $4 million"},
    {limit:10000000,fee:0.20, text:"assets in excess of $10 million"},
  ];

  discountTotal=0;
  feeScheduleRemaining={amt:0,percent:0};
  blendedFeeFromSchedule=0;

  blendedRate = 0;

submit(){
  this.calculateFeeSchedule();
}

  resetFee(): void {
    this._preventClear = true;
    this.feeScheduleForm.patchValue({ discount: 0, flatFee: 0 }, { emitEvent: false });
    this._preventClear = false;
    this._flatFeeExplicit = false;
    this.feeOverrideEnabled = false;
    this.calculateFeeSchedule();
  }

  toggleFeeOverride(): void {
    if (this.feeOverrideEnabled) {
      this.resetFee();
    } else {
      this.feeOverrideEnabled = true;
    }
  }

  calculateFeeSchedule(){
    this.discountTotal = +(this.feeScheduleForm.get('discount')?.value ?? 0);
    this.initialBalance = +(this.feeScheduleForm.get('initialBalance')?.value ?? 0);
    const flatFeeVal = +(this.feeScheduleForm.get('flatFee')?.value ?? 0);
    const flatFeeActive = this._flatFeeExplicit || flatFeeVal > 0;

    // Compute original (undiscounted) blended fee for implied-discount calculation
    let originalTotalFee = 0;
    let origRemaining = this.initialBalance;
    for (let i = 0; i < this.feeSchedule.length; i++) {
      const amt = Math.min(Math.max(origRemaining, 0), this.feeSchedule[i].limit);
      originalTotalFee += (amt * this.feeSchedule[i].fee) / (this.initialBalance || 1);
      origRemaining -= this.feeSchedule[i].limit;
    }
    if (origRemaining > 0) {
      originalTotalFee += (origRemaining * this.feeSchedule[this.feeSchedule.length - 1].fee) / (this.initialBalance || 1);
    }

    let totalFee = 0;
    let dollar_remaining = this.initialBalance;
    let fee = 0;
    let feeDiscounted = 0;
    this.blendedRate=0;
    for (let i = 0; i < this.feeSchedule.length; i++)
    {
        let tier =  this.feeSchedule[i].limit;
        fee = this.feeSchedule[i].fee;

        this.feeSchedule[i].discountPercent = flatFeeActive
          ? (1 - flatFeeVal / this.feeSchedule[i].fee) * 100
          : this.discountTotal;

        this.feeSchedule[i].feeDiscounted = flatFeeActive
          ? flatFeeVal
          : this.feeSchedule[i].fee * (1 - this.discountTotal / 100);

        feeDiscounted = this.feeSchedule[i].feeDiscounted;
        if (dollar_remaining >= tier)
        {
            totalFee += (tier * feeDiscounted) / this.initialBalance;
            if(i==this.feeSchedule.length-1){
              this.feeSchedule[i].amt = dollar_remaining;
            }
            else{
              this.feeSchedule[i].amt = this.feeSchedule[i].limit;
            }
            this.feeSchedule[i].percent = this.feeSchedule[i].amt/this.initialBalance;
        }
        else if (dollar_remaining > 0)
        {
            totalFee += (dollar_remaining * feeDiscounted) / this.initialBalance;
            this.feeSchedule[i].amt = dollar_remaining;
            this.feeSchedule[i].percent = this.feeSchedule[i].amt/this.initialBalance;
        }
        else
        {
          this.feeSchedule[i].amt  = 0;
          this.feeSchedule[i].percent = 0;
            //break;
        }
        dollar_remaining -= tier;
        this.blendedRate+=feeDiscounted;
    }
    this.blendedRate=this.blendedRate/this.feeSchedule.length;
    if (dollar_remaining > 0)
    {
        totalFee += (dollar_remaining * feeDiscounted) / this.initialBalance;
        
        this.feeScheduleRemaining.amt =dollar_remaining;
        this.feeScheduleRemaining.percent =this.feeScheduleRemaining.amt/this.initialBalance;        
    }

    this.blendedFeeFromSchedule=totalFee;
    this.hasDiscountOrFlatFee = flatFeeActive || this.discountTotal > 0;

    // When flat fee is active, reflect the implied blended discount in the discount input
    if (flatFeeActive && originalTotalFee > 0) {
      const impliedDiscount = (1 - totalFee / originalTotalFee) * 100;
      this._preventClear = true;
      this.feeScheduleForm.patchValue({ discount: Math.round(impliedDiscount * 100) / 100 }, { emitEvent: false });
      this._preventClear = false;
    }

    this.annualPercentageFee.emit(this.blendedFeeFromSchedule);
    this.flatFeeChange.emit(flatFeeActive ? flatFeeVal : null);
    this.discountChange.emit(this.discountTotal);

    this.cdr.detectChanges();
    
  }


}
